import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, Paperclip, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useManagerDecideLeaveMutation, useWorkplaceApprovalsQuery } from '@/client/api/hr/people';
import { useManagerDecideExpenseMutation, useOpenDocument } from '@/client/api/business/spend';
import type { Expense } from '@/client/types/business/spend';
import { DecisionDialog } from '../HumanResource/components/DecisionDialog';
import { money } from '../HumanResource/components/comp-format';
import type { TimeOffRequest, WorkplacePerson } from '@/client/types/hr';
import { EmployeeAvatar, EmptyState, Panel } from '../HumanResource/components/hr-ui';
import { LeaveDecisionDialog } from '../HumanResource/components/LeaveDecisionDialog';
import { formatDay, formatRange } from '../HumanResource/components/leave-format';

/** A manager's inbox: their direct reports' pending time off and expense
 *  claims, and who is out soon. The API only returns the caller's own reports. */
export function ApprovalsTab({ reports }: { reports: WorkplacePerson[] }) {
  const approvalsQ = useWorkplaceApprovalsQuery();
  const decide = useManagerDecideLeaveMutation();
  const [deciding, setDeciding] = useState<{ request: TimeOffRequest; decision: 'approve' | 'deny' } | null>(null);
  const person = (id: string) => reports.find((p) => p.id === id);
  const decideExpense = useManagerDecideExpenseMutation();
  const openDoc = useOpenDocument('workplace');
  const [expenseDecision, setExpenseDecision] = useState<{ expense: Expense; approve: boolean } | null>(null);

  const confirm = (note: string) => {
    if (!deciding) return;
    const { request, decision } = deciding;
    decide.mutate(
      { id: request.id, decision, note },
      {
        onSuccess: () => {
          toast.success(decision === 'approve' ? 'Approved' : 'Denied');
          setDeciding(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not record the decision'),
      },
    );
  };

  if (approvalsQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-14 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your team's requests…
      </div>
    );
  }
  if (approvalsQ.isError || !approvalsQ.data) {
    return <EmptyState label={`Could not load approvals: ${(approvalsQ.error as Error)?.message ?? 'unknown error'}`} />;
  }
  const { pending, upcoming } = approvalsQ.data;
  const expenses = approvalsQ.data.expenses ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {expenses.length > 0 && (
        <Panel title={`Expense claims waiting for you (${expenses.length})`} className="lg:col-span-2">
          <ul className="divide-y divide-gray-50">
            {expenses.map((x) => {
              const p = person(x.employeeId);
              return (
                <li key={x.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                  <div className="flex min-w-0 items-start gap-3">
                    <EmployeeAvatar employee={p} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900">
                        {p?.name ?? 'Team member'} · {money(x.amount, x.currency)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {x.description} · {formatDay(x.spentOn)}
                        {!x.hasReceipt && ' · no receipt'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    {x.hasReceipt && (
                      <Button size="sm" variant="ghost" className="gap-1" onClick={() => openDoc(`/expenses/${x.id}/receipt`).catch((e) => toast.error((e as Error).message))}>
                        <Paperclip className="h-3.5 w-3.5" /> Receipt
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="gap-1 text-red-600 hover:text-red-700" onClick={() => setExpenseDecision({ expense: x, approve: false })}>
                      <XIcon className="h-3.5 w-3.5" /> Reject
                    </Button>
                    <Button size="sm" className="gap-1" onClick={() => setExpenseDecision({ expense: x, approve: true })}>
                      <Check className="h-3.5 w-3.5" /> Approve
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Panel>
      )}
      <Panel title={`Waiting for you (${pending.length})`}>
        {pending.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">Nothing to decide. Your team's requests show up here.</p>
        ) : (
          <ul className="space-y-3">
            {pending.map((r) => {
              const p = person(r.employeeId);
              return (
                <li key={r.id} className="rounded-lg border border-gray-100 p-3">
                  <div className="flex items-start gap-3">
                    <EmployeeAvatar employee={p} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">{p?.name ?? 'Someone on your team'}</p>
                      <p className="text-xs text-gray-500">
                        {r.typeName} · {formatRange(r)} · {r.days} working day{r.days === 1 ? '' : 's'}
                      </p>
                      {r.reason && <p className="mt-1 text-xs text-gray-600">“{r.reason}”</p>}
                      <p className="mt-1 text-[11px] text-gray-400">Asked {formatDay(r.requestedAt)}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-red-600 hover:text-red-700"
                      onClick={() => setDeciding({ request: r, decision: 'deny' })}
                    >
                      <XIcon className="h-3.5 w-3.5" /> Deny
                    </Button>
                    <Button size="sm" className="gap-1.5" onClick={() => setDeciding({ request: r, decision: 'approve' })}>
                      <Check className="h-3.5 w-3.5" /> Approve
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel title="Who's out soon">
        {upcoming.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">Nobody on your team has approved time off coming up.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {upcoming.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2">
                <EmployeeAvatar employee={person(r.employeeId)} size="sm" />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800">{person(r.employeeId)?.name ?? 'Team member'}</p>
                  <p className="text-xs text-gray-500">
                    {r.typeName} · {formatRange(r)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <DecisionDialog
        open={!!expenseDecision}
        approve={expenseDecision?.approve ?? true}
        title={expenseDecision?.approve ? 'Approve this claim?' : 'Reject this claim?'}
        description={
          expenseDecision
            ? `${person(expenseDecision.expense.employeeId)?.name ?? 'Your report'}: ${expenseDecision.expense.description}, ${money(expenseDecision.expense.amount, expenseDecision.expense.currency)}. Accounting pays approved claims back.`
            : ''
        }
        busy={decideExpense.isPending}
        onCancel={() => setExpenseDecision(null)}
        onConfirm={(note) => {
          if (expenseDecision)
            decideExpense.mutate(
              { id: expenseDecision.expense.id, decision: expenseDecision.approve ? 'approve' : 'reject', note },
              { onSuccess: () => toast.success(expenseDecision.approve ? 'Approved' : 'Rejected'), onError: (e) => toast.error((e as Error).message) },
            );
          setExpenseDecision(null);
        }}
      />

      <LeaveDecisionDialog
        pending={deciding}
        who={person(deciding?.request.employeeId ?? '')?.name ?? 'Your report'}
        busy={decide.isPending}
        onCancel={() => setDeciding(null)}
        onConfirm={confirm}
      />
    </div>
  );
}
